/**
 * useRealtimeWithFallback Hook
 *
 * Provides a polling fallback when realtime connection is unavailable.
 * Makes realtime the primary mechanism and only polls when disconnected.
 */

import { useEffect, useRef, useCallback } from 'react';

interface UseRealtimeWithFallbackOptions<T> {
  /** Function to fetch fresh data from the server */
  fetchData: () => Promise<T>;
  /** Callback when new data is available */
  onData: (data: T) => void;
  /** Whether realtime connection is currently active */
  realtimeConnected: boolean;
  /** Poll interval in ms when realtime is disconnected (default: 3000) */
  pollInterval?: number;
  /** Whether polling should be enabled at all (default: true) */
  enabled?: boolean;
}

/**
 * Hook that polls for data only when realtime is disconnected.
 *
 * @example
 * ```tsx
 * useRealtimeWithFallback({
 *   fetchData: () => manager.getParticipants(),
 *   onData: setParticipants,
 *   realtimeConnected: isConnected,
 *   pollInterval: 3000,
 * });
 * ```
 */
export function useRealtimeWithFallback<T>({
  fetchData,
  onData,
  realtimeConnected,
  pollInterval = 3000,
  enabled = true,
}: UseRealtimeWithFallbackOptions<T>): void {
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isMountedRef = useRef(true);

  // Stable callback refs to avoid re-creating interval
  const fetchDataRef = useRef(fetchData);
  const onDataRef = useRef(onData);
  fetchDataRef.current = fetchData;
  onDataRef.current = onData;

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    // Don't start if already polling
    if (pollIntervalRef.current) return;

    pollIntervalRef.current = setInterval(async () => {
      if (!isMountedRef.current) return;

      try {
        const data = await fetchDataRef.current();
        if (isMountedRef.current) {
          onDataRef.current(data);
        }
      } catch (err) {
        console.error('Fallback polling error:', err);
      }
    }, pollInterval);
  }, [pollInterval]);

  useEffect(() => {
    isMountedRef.current = true;

    if (!enabled) {
      stopPolling();
      return;
    }

    // Only poll when realtime is disconnected
    if (!realtimeConnected) {
      console.log('📡 Realtime disconnected - starting fallback polling');
      startPolling();
    } else {
      console.log('📡 Realtime connected - stopping fallback polling');
      stopPolling();
    }

    return () => {
      isMountedRef.current = false;
      stopPolling();
    };
  }, [realtimeConnected, enabled, startPolling, stopPolling]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      stopPolling();
    };
  }, [stopPolling]);
}

/**
 * Hook for periodic polling regardless of realtime status.
 * Use this when you always want to poll (e.g., for leaderboard during racing).
 */
export function usePolling<T>({
  fetchData,
  onData,
  interval,
  enabled = true,
}: {
  fetchData: () => Promise<T>;
  onData: (data: T) => void;
  interval: number;
  enabled?: boolean;
}): { stop: () => void; start: () => void } {
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isMountedRef = useRef(true);

  const fetchDataRef = useRef(fetchData);
  const onDataRef = useRef(onData);
  fetchDataRef.current = fetchData;
  onDataRef.current = onData;

  const stop = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  const start = useCallback(() => {
    if (pollIntervalRef.current) return;

    pollIntervalRef.current = setInterval(async () => {
      if (!isMountedRef.current) return;

      try {
        const data = await fetchDataRef.current();
        if (isMountedRef.current) {
          onDataRef.current(data);
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, interval);
  }, [interval]);

  useEffect(() => {
    isMountedRef.current = true;

    if (enabled) {
      start();
    } else {
      stop();
    }

    return () => {
      isMountedRef.current = false;
      stop();
    };
  }, [enabled, start, stop]);

  return { stop, start };
}
